// One account row inside a `ConnectionGroup`, `#s01`. Always shows the
// display name, `•last4`, a business chip when `business_use !== 'none'`,
// the class label ("Depository"/"Credit"/"Loan"/"Investment"), a per-account
// status caption ("as of 12 Aug" / "never reported a balance"), and the
// SIGNED value through `AmountText` -- nothing here negates by `kind`.
// `dense` selects the tablet/desktop single-line layout with an inline type
// column; the phone layout stacks the status caption under the value
// instead, per the responsive plan's "information is never dropped" rule.
import { Link, useLocation } from 'react-router-dom';
import type { Account, CanonicalAccount } from '@/api/hooks/useAccounts';
import { accountClassLabel } from '@/api/hooks/useAccounts';
import { AmountText } from '@/components/AmountText';
import { BusinessChip } from '@/components/BusinessChip';
import { formatHumanDate } from '@/lib/date';
import { scopeHref } from '@/app/shell/scope';

function statusCaption(account: Account): string | null {
  if (account.balance === null) return 'never reported a balance';
  if (account.balance_status === 'stale' || account.balance_status === 'derived') {
    return account.as_of ? `as of ${formatHumanDate(account.as_of, { includeYear: false })}` : null;
  }
  return null;
}

type AccountRowAccount = Account | CanonicalAccount;

function continuation(account: AccountRowAccount): { accountId: string | null; setupRequired: boolean } {
  if (!('canonical_account_id' in account)) return { accountId: account.account_id, setupRequired: false };
  if (account.canonical_account_id) return { accountId: account.canonical_account_id, setupRequired: false };
  if (account.reconciliation_initialization_required) return { accountId: account.account_id, setupRequired: true };
  return { accountId: null, setupRequired: false };
}

export function AccountRow({ account, dense = false, className }: { account: AccountRowAccount; dense?: boolean; className?: string }) {
  const location = useLocation();
  const caption = statusCaption(account);
  const { accountId, setupRequired } = continuation(account);
  const readScope = new URLSearchParams(location.search).get('scope');
  const diagnosticScope = readScope === 'current' || readScope === 'saved_history' ? readScope : null;
  const rowClassName = [
    'flex min-h-11 flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2 text-sm text-foreground hover:bg-muted/50',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const rowContent = (
    <>
      <span className="flex min-w-0 flex-[1_1_12rem] flex-wrap items-start gap-x-2 gap-y-1">
        <span className="min-w-0 whitespace-normal break-words">
          {account.display_name} <span className="text-muted-foreground">•{account.last4 ?? '----'}</span>
        </span>
        {account.business_use !== 'none' && <BusinessChip />}
        {dense && <span className="text-xs text-muted-foreground">{accountClassLabel(account.kind)}</span>}
      </span>
      <span className="ml-auto flex max-w-full shrink-0 flex-col items-end gap-1">
        <AmountText
          value={account.balance}
          currency={account.currency}
          variant="balance"
          balanceStatus={account.balance_status}
          asOf={undefined}
        />
        {setupRequired && <span className="max-w-full whitespace-normal break-words text-right text-xs leading-4 text-degraded-foreground">setup required</span>}
        {!setupRequired && caption && <span className="max-w-full whitespace-normal break-words text-right text-xs leading-4 text-degraded-foreground">{caption}</span>}
        {!accountId && <span className="max-w-full whitespace-normal break-words text-right text-xs leading-4 text-degraded-foreground">account unavailable</span>}
      </span>
    </>
  );

  if (!accountId) {
    return <div data-testid="account-row" className={rowClassName}>{rowContent}</div>;
  }
  return (
    <Link
      to={scopeHref(`/accounts/${accountId}${location.hash}`, location.search, { account: accountId, readScope: diagnosticScope })}
      data-testid="account-row"
      data-account-id={accountId}
      className={rowClassName}
    >
      {rowContent}
    </Link>
  );
}

export default AccountRow;
